import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import createWebStorage from "redux-persist/lib/storage/createWebStorage";

import authReducer from "./slices/authSlice";
import { baseApi } from "./baseApi";
import cartReducer from "./slices/cartSlice";
import { listenerMiddleware } from "./middleware/listenerMiddleware";
import uiReducer from "./slices/uiSlice";
import wishlistReducer from "./slices/wishlistSlice";

const createNoopStorage = () => ({
  getItem() {
    return Promise.resolve(null);
  },
  setItem(_key: string, value: string) {
    return Promise.resolve(value);
  },
  removeItem() {
    return Promise.resolve();
  },
});

const persistStorage =
  typeof window === "undefined"
    ? createNoopStorage()
    : createWebStorage("local");

// ─── Persist Config ───────────────────────────────────────────
// auth مش محتاجة persist — الـ access token بيتخزن في memory بس
// الـ refresh token موجود في httpOnly cookie بيتعمله handle في interceptors
// cart مش محتاجة persist — كل البيانات من backend session
const wishlistPersistConfig = {
  key: "wishlist",
  storage: persistStorage,
  version: 1,
};

// ─── Root Reducer ─────────────────────────────────────────────
const rootReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  auth: authReducer,
  cart: cartReducer,
  ui: uiReducer,
  wishlist: persistReducer(wishlistPersistConfig, wishlistReducer),
});

// ─── Store ────────────────────────────────────────────────────
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // redux-persist بتستخدم actions غير serializable — نتجاهلها
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .prepend(listenerMiddleware.middleware)
      .concat(baseApi.middleware),
  devTools: process.env.NODE_ENV !== "production",
});

setupListeners(store.dispatch);

export const persistor = persistStore(store);

// ─── Types ────────────────────────────────────────────────────
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;
